import React, { useState, useEffect } from 'react';
import { 
  Brain, TrendingUp, TrendingDown, BarChart3, RefreshCw, 
  CheckCircle, XCircle, Edit2, Zap, AlertTriangle, 
  ChevronRight, Loader2, Activity, Target, Award
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MLAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [improvements, setImprovements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingImprovements, setLoadingImprovements] = useState(false);
  const [days, setDays] = useState(30);

  const categories = [
    { id: 'referto', name: 'Referti', icon: '📋', color: 'blue' },
    { id: 'diagnosi', name: 'Diagnosi', icon: '🔬', color: 'purple' },
    { id: 'terapia', name: 'Terapie', icon: '💊', color: 'green' },
    { id: 'anamnesi', name: 'Anamnesi', icon: '📝', color: 'yellow' },
    { id: 'trascrizione', name: 'Trascrizioni', icon: '🎤', color: 'red' },
    { id: 'blog', name: 'Blog', icon: '📰', color: 'indigo' },
    { id: 'social', name: 'Social', icon: '📱', color: 'pink' },
    { id: 'newsletter', name: 'Newsletter', icon: '📧', color: 'cyan' },
    { id: 'chatbot', name: 'Chatbot', icon: '🤖', color: 'orange' },
    { id: 'immagine', name: 'Immagini', icon: '🖼️', color: 'teal' },
    { id: 'video', name: 'Video', icon: '🎬', color: 'violet' },
  ];

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    loadAnalytics();
  }, [days]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ml/prompt-analytics?days=${days}`,
        getAuthHeaders()
      );
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      toast.error('Errore caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async (category) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ml/prompt-versions?category=${category}`,
        getAuthHeaders()
      );
      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      }
    } catch (error) {
      toast.error('Errore caricamento versioni');
    }
  };

  const loadImprovements = async (category) => {
    setLoadingImprovements(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ml/prompt-improvement/${category}`,
        getAuthHeaders()
      );
      if (response.ok) {
        const data = await response.json();
        setImprovements(data);
      }
    } catch (error) {
      toast.error('Errore caricamento suggerimenti');
    } finally {
      setLoadingImprovements(false);
    }
  };

  const handleCategorySelect = async (categoryId) => {
    setSelectedCategory(categoryId);
    await Promise.all([
      loadVersions(categoryId),
      loadImprovements(categoryId)
    ]);
  };

  const activateVersion = async (promptId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ml/prompt-versions/${promptId}/activate`,
        {
          method: 'PUT',
          ...getAuthHeaders()
        }
      );
      if (response.ok) {
        toast.success('Versione attivata');
        loadVersions(selectedCategory);
        loadAnalytics();
      }
    } catch (error) {
      toast.error('Errore attivazione');
    }
  };

  const getCategoryStats = (categoryId) => {
    if (!analytics?.categories?.[categoryId]) return null;
    const cat = analytics.categories[categoryId];
    const activeVersion = cat.versions?.find(v => v.is_active);
    return {
      totalUses: cat.total_uses || 0,
      positiveRate: activeVersion?.positive_rate || 0,
      avgQuality: activeVersion?.avg_quality_score || 0,
      versionsCount: cat.versions?.length || 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ml-analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Brain className="w-7 h-7 text-[#2A9FCB]" />
            ML Prompt Analytics
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Monitora e ottimizza le performance dei prompt AI
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="days-filter"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
            <option value={365}>Ultimo anno</option>
          </select>
          
          <button
            onClick={loadAnalytics}
            className="flex items-center gap-2 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#1e7ba8] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Feedback Totali</p>
              <p className="text-3xl font-bold text-gray-800">{analytics?.total_feedback || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Top Performers</p>
              <p className="text-3xl font-bold text-[#2A9FCB]">{analytics?.top_performers?.length || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[#E8F4F8] rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-[#2A9FCB]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Da Migliorare</p>
              <p className="text-3xl font-bold text-amber-600">{analytics?.needs_improvement?.length || 0}</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Categorie Attive</p>
              <p className="text-3xl font-bold text-purple-600">{Object.keys(analytics?.categories || {}).length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Categorie Prompt</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categories.map((cat) => {
            const stats = getCategoryStats(cat.id);
            const isSelected = selectedCategory === cat.id;
            
            return (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat.id)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected 
                    ? 'border-[#2A9FCB] bg-[#2A9FCB]/5 shadow-md' 
                    : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <div className="text-2xl mb-2">{cat.icon}</div>
                <div className="font-medium text-gray-800 text-sm">{cat.name}</div>
                {stats && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-gray-500">
                      {stats.totalUses} utilizzi
                    </div>
                    {stats.positiveRate > 0 && (
                      <div className={`text-xs font-medium ${
                        stats.positiveRate >= 80 ? 'text-[#2A9FCB]' : 
                        stats.positiveRate >= 50 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {stats.positiveRate.toFixed(0)}% positivi
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Category Details */}
      {selectedCategory && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Versions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#2A9FCB]" />
              Versioni Prompt - {categories.find(c => c.id === selectedCategory)?.name}
            </h3>
            
            {versions.length === 0 ? (
              <p className="text-gray-500 text-sm">Nessuna versione trovata</p>
            ) : (
              <div className="space-y-3">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className={`p-4 rounded-lg border ${
                      version.is_active 
                        ? 'border-[#2A9FCB] bg-[#2A9FCB]/5' 
                        : 'border-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{version.version_id}</span>
                        {version.is_active && (
                          <span className="px-2 py-0.5 bg-[#2A9FCB] text-white text-xs rounded-full">
                            Attivo
                          </span>
                        )}
                      </div>
                      {!version.is_active && (
                        <button
                          onClick={() => activateVersion(version.id)}
                          className="text-sm text-[#2A9FCB] hover:underline"
                        >
                          Attiva
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Utilizzi:</span>
                        <span className="ml-1 font-medium">{version.total_uses || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Positivi:</span>
                        <span className="ml-1 font-medium text-[#2A9FCB]">
                          {version.positive_feedback || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Qualità:</span>
                        <span className="ml-1 font-medium">
                          {(version.avg_quality_score || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#2A9FCB] to-[#3d9fcb] rounded-full transition-all"
                          style={{ width: `${version.positive_rate || 0}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{(version.positive_rate || 0).toFixed(0)}% tasso positivo</span>
                        <span>{version.modified_feedback || 0} modificati</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Improvement Suggestions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Suggerimenti Miglioramento
            </h3>
            
            {loadingImprovements ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : improvements?.status === 'insufficient_data' ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-600 text-sm">{improvements.message}</p>
                <p className="text-gray-500 text-xs mt-2">
                  Feedback raccolti: {improvements.feedback_count}/5 minimi
                </p>
              </div>
            ) : improvements?.suggestions?.length > 0 ? (
              <div className="space-y-3">
                {improvements.suggestions.map((suggestion, idx) => (
                  <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 text-sm">
                          {suggestion.type === 'missing_content' ? 'Contenuto Mancante' :
                           suggestion.type === 'high_rejection' ? 'Alto Tasso Rifiuto' :
                           'Suggerimento'}
                        </p>
                        <p className="text-amber-700 text-sm mt-1">{suggestion.description}</p>
                        <p className="text-amber-600 text-xs mt-2 italic">{suggestion.recommendation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#E8F4F8] rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#2A9FCB]" />
                <div>
                  <p className="font-medium text-[#2c4667]">Nessun problema rilevato</p>
                  <p className="text-[#2A9FCB] text-sm">I prompt stanno performando bene!</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Performers & Needs Improvement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#2A9FCB]" />
            Top Performers
          </h3>
          
          {analytics?.top_performers?.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_performers.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-[#E8F4F8] rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {categories.find(c => c.id === item.category)?.icon}
                    </span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{item.version}</p>
                      <p className="text-gray-500 text-xs">
                        {categories.find(c => c.id === item.category)?.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#2A9FCB]">{item.positive_rate}%</p>
                    <p className="text-gray-500 text-xs">positivi</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              Non ci sono ancora abbastanza dati per identificare i top performers
            </p>
          )}
        </div>

        {/* Needs Improvement */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            Da Migliorare
          </h3>
          
          {analytics?.needs_improvement?.length > 0 ? (
            <div className="space-y-3">
              {analytics.needs_improvement.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {categories.find(c => c.id === item.category)?.icon}
                    </span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{item.version}</p>
                      <p className="text-gray-500 text-xs">
                        {categories.find(c => c.id === item.category)?.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{item.positive_rate}%</p>
                    <p className="text-gray-500 text-xs">positivi</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              Tutti i prompt stanno performando sopra la soglia minima
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MLAnalyticsDashboard;
